import React, { useState } from 'react';
import { Modal, Checkbox, Button, message } from 'antd';
import { joinVerifiedPlus } from 'api/verifiedPlus';
import s from './s.module.less';

import modalImg from 'assets/home/verifyPlus/modal-img.png';

type Props = {
    open: boolean;
    onCancel: () => void;
    onJoinSuccess: () => void;
};

const AGREEMENT_VERSION = '1.0';

const JoinVerifyPlusModal = ({ open, onCancel, onJoinSuccess }: Props) => {
    const [confirmed, setConfirmed] = useState(false);
    const [showConfirmHint, setShowConfirmHint] = useState(false);
    const [joining, setJoining] = useState(false);

    const handleJoin = async () => {
        if (!confirmed) {
            setShowConfirmHint(true);
            return;
        }
        try {
            setJoining(true);
            const res = await joinVerifiedPlus({
                agreementAccepted: true,
                agreementVersion: AGREEMENT_VERSION,
            });
            if (res && !res.error) {
                onJoinSuccess();
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to join. Please try again.');
        } finally {
            setJoining(false);
        }
    };

    const handleCancel = () => {
        setConfirmed(false);
        setShowConfirmHint(false);
        onCancel();
    };

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            footer={null}
            width={800}
            destroyOnClose
            title={null}
            centered
            closable
            className={s.modalWrap}
        >
            <div className={s.content}>
                <div className={s.header}>
                    <span className={s.newBadge}>New</span>
                    <div className={s.title}>Try Klarity Verified Plus for one month</div>
                    <div className={s.description}>
                        Every new patient adds a valid payment card before booking, so copays, cash-pays and balances
                        can be charged to a card that&apos;s already there.
                    </div>
                </div>

                <div className={s.scrollBody}>
                    <div className={s.section}>
                        <div className={s.sectionTitle}>What changes versus the current program</div>
                        <ul className={s.sectionList}>
                            <li>
                                Card collection is mandatory for all of your new-patient bookings, and Klarity will
                                charge $0.5 to validate the patient&apos;s card at booking (will be refunded to patient
                                immediately after verification).
                            </li>
                            <li>
                                Every new-patient booking is billable, even if they cancel within 24 hours of bookings.
                            </li>
                            <li>
                                If you opt in, you stay on the test for one month. After that, you go back to your
                                current program until Klarity announces what's next.
                            </li>
                        </ul>
                    </div>

                    <div className={s.illustrationWrap}>
                        <img src={modalImg} alt="" className={s.illustrationImg} />
                    </div>

                    <div className={s.section}>
                        <div className={s.sectionTitle}>What stays the same versus the current program</div>
                        <ul className={s.sectionList}>
                            <li>Every new patient needs to verify phone number at booking</li>
                            <li>Your current lead-generation fee amount</li>
                            <li>Klarity continues to charge your card on file 24 hours post booking</li>
                        </ul>
                    </div>
                </div>

                <div className={s.fixedBottom}>
                    <div className={s.confirmWrap}>
                        <Checkbox
                            checked={confirmed}
                            onChange={(e) => {
                                setConfirmed(e.target.checked);
                                if (e.target.checked) {
                                    setShowConfirmHint(false);
                                }
                            }}
                        >
                            I understand that in this test, every new-patient booking is billable, even if they cancel
                            within 24 hours of bookings.
                        </Checkbox>
                        {showConfirmHint && !confirmed && (
                            <div className={s.confirmHint}>Confirm this before joining</div>
                        )}
                    </div>

                    <div className={s.actions}>
                        <Button type="primary" loading={joining} onClick={handleJoin} className={s.joinButton}>
                            Join the one-month test
                        </Button>
                        <Button onClick={handleCancel} className={s.keepButton}>
                            Keep my current program
                        </Button>
                    </div>

                    <div className={s.footer}>
                        To leave the program, email us at{' '}
                        <a href="mailto:providersupport@helloklarity.com">providersupport@helloklarity.com</a>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default JoinVerifyPlusModal;
